/**
 * DataBest — PerfAnomalyPanel
 * Displays anomaly flags for a caregiver in the detail view.
 * Each flag is a card with severity color, title, threshold context,
 * example measurement IDs, and a constructive suggestion.
 */
import React from "react";
import {
    AlertTriangle,
    AlertOctagon,
    CheckCircle2,
    Clock,
    MessageSquareX,
    Ban,
} from "lucide-react";

const SEVERITY_CFG = {
    red: {
        bg: "bg-red-50",
        border: "border-red-200",
        text: "text-red-800",
        icon: AlertOctagon,
        label: "Red flag",
    },
    yellow: {
        bg: "bg-amber-50",
        border: "border-amber-200",
        text: "text-amber-800",
        icon: AlertTriangle,
        label: "Yellow flag",
    },
    green: {
        bg: "bg-brand-green-50",
        border: "border-brand-green-200",
        text: "text-brand-green-800",
        icon: CheckCircle2,
        label: "OK",
    },
};

const KEY_ICONS = {
    all_max_streak: AlertTriangle,
    all_min_streak: AlertTriangle,
    identical_comment_streak: MessageSquareX,
    prohibited_word_hits: Ban,
    after_hours_activity: Clock,
};

const PerfAnomalyPanel = ({ flags = [] }) => {
    return (
        <div
            className="rounded-2xl border border-slate-200 bg-white shadow-sm"
            data-testid="perf-anomaly-panel"
        >
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
                <p className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-slate-500">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Anomaly flags
                </p>
                <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${
                        flags.length === 0
                            ? "bg-brand-green-100 text-brand-green-800"
                            : flags.some((f) => f.severity === "red")
                              ? "bg-red-100 text-red-800"
                              : "bg-amber-100 text-amber-800"
                    }`}
                >
                    {flags.length === 0
                        ? "All clear"
                        : `${flags.length} flag${flags.length === 1 ? "" : "s"}`}
                </span>
            </div>

            {flags.length === 0 ? (
                <div className="p-6 text-center">
                    <CheckCircle2 className="mx-auto mb-1.5 h-6 w-6 text-brand-green-500" />
                    <p className="text-xs font-semibold text-slate-500">
                        No anomalies detected in this period.
                    </p>
                    <p className="mt-0.5 text-[10px] text-slate-400">
                        Scoring pattern looks healthy across the 5 detectors.
                    </p>
                </div>
            ) : (
                <div className="space-y-2 p-3">
                    {flags.map((f) => {
                        const cfg = SEVERITY_CFG[f.severity] || SEVERITY_CFG.yellow;
                        const KeyIcon = KEY_ICONS[f.key] || cfg.icon;
                        return (
                            <div
                                key={f.key}
                                data-testid={`perf-anomaly-${f.key}`}
                                className={`rounded-xl border ${cfg.border} ${cfg.bg} p-3`}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex items-start gap-2">
                                        <KeyIcon
                                            className={`mt-0.5 h-4 w-4 ${cfg.text}`}
                                        />
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <p className={`text-sm font-bold ${cfg.text}`}>
                                                    {f.title}
                                                </p>
                                                <span
                                                    className={`rounded-full border ${cfg.border} bg-white px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider ${cfg.text}`}
                                                >
                                                    {cfg.label}
                                                </span>
                                            </div>
                                            <p className="mt-0.5 text-[11px] text-slate-600">
                                                {f.suggestion}
                                            </p>
                                            <p className="mt-1 text-[10px] font-mono text-slate-400">
                                                value: {f.value} · threshold:{" "}
                                                {f.threshold}
                                                {f.examples?.length > 0 && (
                                                    <>
                                                        {" · examples: "}
                                                        {f.examples
                                                            .slice(0, 3)
                                                            .map((id) =>
                                                                id.slice(0, 8),
                                                            )
                                                            .join(", ")}
                                                    </>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default PerfAnomalyPanel;
